import type { ComponentProps, ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import SolidUsdCircleIcon from 'LuxKit/Icons/solid/SolidUsdCircleIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusLogoAsset from 'luxPlus/components/Logos/LuxPlusLogo'

// Cards sit in a `grid-auto-flow: column` carousel with `auto` rows, so they stretch to
// match the tallest card. A floor keeps the shared rhythm without clipping a card whose
// inclusion list is longer than the others.
const OptionCard = styled(Sheet)`
  display: flex;
  flex-direction: column;
  min-height: ${rem(376)};
`

const OptionSection = styled(Sheet)`
  padding-block: ${rem(16)};
`

const OptionBody = styled(Sheet)`
  flex: 1;
  padding-block: ${rem(16)};
  border-block: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const FarePrice = styled.div`
  &.lux-plus span {
    color: ${props => props.theme.palette.product.luxPlus.foreground};
  }
`

const LuxPlusLogo = styled(LuxPlusLogoAsset)`
  margin-inline-end: ${rem(3)};
`

const FareRulesLink = styled(TextLink)`
  display: block;
  margin-block-start: ${rem(4)};
`

export interface FareFamilyUpsellOptionFeature {
  code: string
  description: ReactNode
  status: App.FareFamilyFeatureStatus
}

export interface FareFamilyUpsellOptionPrice {
  value: number
  previousValue?: number
  caption?: ReactNode
  secondaryCaption?: ReactNode
  showLuxPlusLogo?: boolean
  isLuxPlus?: boolean
  saleUnit?: string
  signDisplay?: ComponentProps<typeof PriceRowPrice>['signDisplay']
}

export interface FareFamilyUpsellOptionCardProps {
  cabinLabel: string
  commission?: ReactNode
  fareRulesLabel: string
  features: Array<FareFamilyUpsellOptionFeature>
  loyaltyPoints?: ReactNode
  onSelect: () => void
  onViewFareRules: () => void
  price?: FareFamilyUpsellOptionPrice
  selected?: boolean
  title: string
}

function getFeatureIcon(status: App.FareFamilyFeatureStatus) {
  if (status === 'INCLUDED') {
    return <SolidCheckCircleIcon colour="highlight-primary-dark" size="S" />
  }

  if (status === 'CHARGED') {
    return <SolidUsdCircleIcon colour="neutral-two" size="S" />
  }

  return <LineTimesIcon size="S" colour="neutral-two" />
}

function FareFamilyUpsellOptionCard({
  cabinLabel,
  commission,
  fareRulesLabel,
  features,
  loyaltyPoints,
  onSelect,
  onViewFareRules,
  price,
  selected = false,
  title,
}: FareFamilyUpsellOptionCardProps) {
  const intl = useIntl()
  const selectLabel = intl.formatMessage({
    id: 'aBpkOlYk7Trbga9k4IiO',
    defaultMessage: 'Select',
    description: 'Button text to select a flight in desktop tile component',
  })

  return <OptionCard
    background="normal-background"
    border={selected ? 'normal-contrast' : 'light-foreground'}
    borderRadius="M"
    data-selected={selected}
    data-testid="fare-family-option"
    padding="0 16"
  >
    <OptionSection>
      <Group direction="horizontal" fullWidth horizontalAlign="space-between" verticalAlign="center">
        <Group direction="vertical" gap={0}>
          <Heading variant="heading6">{title}</Heading>
          <Caption variant="smallalt">{cabinLabel}</Caption>
        </Group>

        <TextButton kind="secondary" shape="round" onClick={onSelect}>{selectLabel}</TextButton>
      </Group>
    </OptionSection>

    <OptionBody>
      {features.length > 0 && <Group direction="vertical" gap={4}>
        {features.map(feature => <BodyTextBlock
          colour="light-foreground-alt"
          data-testid={`fare-family-feature-${feature.code}`}
          key={feature.code}
          startIcon={getFeatureIcon(feature.status)}
          variant="medium"
          weight="normal"
        >
          {feature.description}
        </BodyTextBlock>)}
      </Group>}
      <FareRulesLink onClick={onViewFareRules} size="medium" weight="regular">
        {fareRulesLabel}
      </FareRulesLink>
    </OptionBody>

    <OptionSection>
      <Group direction="vertical" gap={4}>
        {price && <Group direction="vertical" gap={0}>
          <Group direction="horizontal" gap={4} verticalAlign="baseline">
            {price.previousValue !== undefined && <CaptionTextBlock
              variant="large"
              colour="light-foreground-alt"
              weight="normal"
              strikeThrough
            >
              <FormatCurrency value={price.previousValue} />
            </CaptionTextBlock>}

            {price.showLuxPlusLogo ? <Group direction="horizontal" gap={0} verticalAlign="baseline">
              <LuxPlusLogo height={8} aria-hidden="true" />
              <FarePrice className={price.isLuxPlus ? 'lux-plus' : undefined}>
                <PriceRowPrice
                  price={price.value}
                  saleUnit={price.saleUnit ?? ''}
                  signDisplay={price.signDisplay}
                  size="L"
                />
              </FarePrice>
            </Group> : <FarePrice className={price.isLuxPlus ? 'lux-plus' : undefined}>
              <PriceRowPrice
                price={price.value}
                saleUnit={price.saleUnit ?? ''}
                signDisplay={price.signDisplay}
                size="L"
              />
            </FarePrice>}
          </Group>
          {price.caption && <CaptionTextBlock variant="smallalt" weight="normal">{price.caption}</CaptionTextBlock>}
          {price.secondaryCaption && <CaptionTextBlock variant="smallalt" weight="normal">
            {price.secondaryCaption}
          </CaptionTextBlock>}
        </Group>}
        {commission}
        {loyaltyPoints}
      </Group>
    </OptionSection>
  </OptionCard>
}

export default FareFamilyUpsellOptionCard
